import { useEffect, useState } from 'react'
import { Tabs } from '@heroui/react'
import type { SignageMedia } from '../lib/api'
import { playlistOf, tabOf, type SignageTab } from '../lib/signageEdit'
import type { StaffKey } from '../lib/staffText'
import type { Identity } from '../lib/types'
import { Chrome } from '../ui/Chrome'
import { useStaffText } from '../ui/StaffText'
import { CampaignsTab } from './signageCampaigns'
import { useSignageData } from './signageData'
import { LibraryTab } from './signageLibrary'
import { PlaylistsTab } from './signagePlaylists'
import { ScreensTab } from './signageScreens'
import '../styles/signage.css'

const TABS: ReadonlyArray<[SignageTab, StaffKey]> = [
  ['library', 'signage.tab.library'],
  ['playlists', 'signage.tab.playlists'],
  ['campaigns', 'signage.tab.campaigns'],
  ['screens', 'signage.tab.screens'],
]

/** Signage: the shared library, playlists, campaigns and the main displays, for whoever holds signage.manage. */
export function Signage({ identity, onNavigate }: { identity: Identity; onNavigate: (to: string) => void }) {
  const { say } = useStaffText()
  const [hash, setHash] = useState(() => window.location.hash)
  const [seed, setSeed] = useState<SignageMedia[] | null>(null)
  const data = useSignageData()
  useEffect(() => {
    const sync = () => setHash(window.location.hash)
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])
  const go = (to: string) => {
    window.history.replaceState(null, '', `#${to}`)
    setHash(`#${to}`)
  }
  const tab = tabOf(hash)
  const openPlaylist = (id: string) => go(id ? `playlists/${encodeURIComponent(id)}` : 'playlists')
  const canMachines = (identity.permissions as readonly string[]).includes('branch.manage')

  return (
    <Chrome title={say('signage.title')} staff={identity.staff} onNavigate={onNavigate} setup>
      <main className="page signage-page">
        <header className="page__head signage-head">
          <h1 className="page__title">{say('signage.title')}</h1>
          <Tabs selectedKey={tab} onSelectionChange={(key) => go(tabOf(String(key)))}>
            <Tabs.ListContainer>
              <Tabs.List aria-label={say('signage.title')}>
                {TABS.map(([value, label]) => (
                  <Tabs.Tab key={value} id={value}>
                    {say(label)}
                    <Tabs.Indicator />
                  </Tabs.Tab>
                ))}
              </Tabs.List>
            </Tabs.ListContainer>
          </Tabs>
        </header>
        {tab === 'library' && (
          <LibraryTab
            data={data}
            onOpenPlaylist={openPlaylist}
            onNewPlaylist={(files) => {
              setSeed(files)
              openPlaylist('new')
            }}
            onCampaigns={() => go('campaigns')}
          />
        )}
        {tab === 'playlists' && (
          <PlaylistsTab
            data={data}
            openId={playlistOf(hash)}
            seed={seed}
            onOpen={(id) => {
              if (id !== 'new') setSeed(null)
              openPlaylist(id)
            }}
          />
        )}
        {tab === 'campaigns' && <CampaignsTab data={data} onNavigate={onNavigate} />}
        {tab === 'screens' && <ScreensTab data={data} canMachines={canMachines} onNavigate={onNavigate} />}
      </main>
    </Chrome>
  )
}
